import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

export function PaymentProblemBanner() {
  const { user, isTeamMember } = useAuth();
  const { data: status } = useSubscriptionStatus(true);
  const { toast } = useToast();
  const [openingPortal, setOpeningPortal] = useState(false);
  const isPaid = !!user && ["Paid", "Annual", "SearchAndBid"].includes(user.membershipTier || "");

  if (!isPaid || !status?.paymentProblem) return null;

  const updatePayment = async () => {
    if (isTeamMember) return;
    setOpeningPortal(true);
    try {
      const response = await apiRequest("POST", "/api/stripe/create-portal-session");
      const data = await response.json();
      if (!data.url || typeof data.url !== "string") throw new Error("Failed to open billing portal");
      window.location.assign(data.url);
    } catch {
      toast({
        title: "Unable to open billing portal",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setOpeningPortal(false);
    }
  };

  return (
    <div className="px-6 pt-3 flex-shrink-0">
      <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20 mb-4" data-testid="banner-payment-problem">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertTitle className="text-yellow-800 dark:text-yellow-200">Payment problem</AlertTitle>
        <AlertDescription className="text-yellow-700 dark:text-yellow-300">
          <p>Your last payment didn’t go through. Please update your payment method to keep your account active.</p>
          {isTeamMember ? (
            <p className="mt-2">Please ask your account owner to update the payment method</p>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              disabled={openingPortal}
              onClick={updatePayment}
              data-testid="button-update-payment"
            >
              {openingPortal && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Update payment
            </Button>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
}
